// src/components/Contacto.jsx
import React, { useState, useEffect } from "react";
import { Phone, Mail, Linkedin, Instagram, Facebook } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import emailjs from "@emailjs/browser";

export default function Contacto() {
  const [formData, setFormData] = useState({
    nombre: "",
    empresa: "",
    prefijo: "+54",
    telefono: "",
    email: "",
    mensaje: "",
    website: "", // honeypot: campo señuelo para bots, invisible para personas
  });

  const [toast, setToast] = useState(null); // "ok" | "error" | null
  const [enviando, setEnviando] = useState(false);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleTelefonoChange = (e) => {
    const soloNumeros = e.target.value.replace(/\D/g, "");
    setFormData({ ...formData, telefono: soloNumeros });
  };

  const handlePrefijoChange = (e) => {
    // Permite un "+" solo al inicio y dígitos en el resto.
    let valor = e.target.value.replace(/[^\d+]/g, "");
    valor = valor[0] === "+" ? "+" + valor.slice(1).replace(/\+/g, "") : valor.replace(/\+/g, "");
    setFormData({ ...formData, prefijo: valor });
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    // Si el honeypot viene completo, es un bot: ignoramos el envío en silencio.
    if (formData.website) {
      setFormData({ nombre: "", empresa: "", prefijo: "+54", telefono: "", email: "", mensaje: "", website: "" });
      return;
    }

    setEnviando(true);

    emailjs
      .send(
        "service_w8j5xqc",
        "template_7wnu7qx",
        {
          nombre: formData.nombre,
          empresa: formData.empresa,
          telefono: formData.telefono ? `${formData.prefijo} ${formData.telefono}` : "",
          email: formData.email,
          mensaje: formData.mensaje,
        },
        "Flp4BGb0maR2LlMhF"
      )
      .then(() => {
        setFormData({ nombre: "", empresa: "", prefijo: "+54", telefono: "", email: "", mensaje: "", website: "" });
        setToast("ok");
      })
      .catch((error) => {
        console.error("Error:", error);
        setToast("error");
      })
      .finally(() => {
        setEnviando(false);
      });
  };

  // ⏱ Hace que el popup desaparezca solo
  useEffect(() => {
    if (toast) {
      const timer = setTimeout(() => setToast(null), 3500);
      return () => clearTimeout(timer);
    }
  }, [toast]);

  return (
    <>
      {/* 🔔 TOAST POPUP */}
      <AnimatePresence>
        {toast && (
          <motion.div
            initial={{ opacity: 0, y: -80 }}
            animate={{ opacity: 1, y: 20 }}
            exit={{ opacity: 0, y: -80 }}
            transition={{ duration: 0.4 }}
            className={`fixed left-1/2 -translate-x-1/2 top-0 z-50 px-6 py-4 rounded-xl shadow-2xl text-sm font-medium
            ${toast === "ok" ? "bg-green-500 text-white" : "bg-red-500 text-white"}`}
          >
            {toast === "ok"
              ? "Mensaje enviado correctamente ✔"
              : "No se pudo enviar. Intenta nuevamente ✖"}
          </motion.div>
        )}
      </AnimatePresence>

      <section id="contacto" className="relative bg-primary text-white pt-16 pb-24">
        <div className="max-w-6xl mx-auto px-6 text-center">
          <h2 className="text-4xl md:text-5xl font-bold mb-8">Contacto</h2>

          <div className="flex flex-col md:flex-row md:justify-between items-start md:items-center gap-8 overflow-x-hidden">
            {/* Formulario */}
            <motion.form
              className="w-full md:w-[62%] bg-white text-primary p-6 md:p-8 rounded-2xl shadow-lg"
              onSubmit={handleSubmit}
              initial={{ opacity: 0, y: 50 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
            >
              {/* Honeypot anti-spam: oculto para personas, los bots suelen completarlo */}
              <input
                type="text"
                name="website"
                value={formData.website}
                onChange={handleChange}
                autoComplete="off"
                tabIndex={-1}
                aria-hidden="true"
                className="absolute -left-[9999px] w-px h-px opacity-0"
              />

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label htmlFor="nombre" className="sr-only">Nombre y Apellido</label>
                  <input
                    type="text"
                    id="nombre"
                    name="nombre"
                    placeholder="Nombre y Apellido"
                    value={formData.nombre}
                    onChange={handleChange}
                    className="border border-gray-300 rounded-lg p-3 w-full focus:outline-none focus:ring-2 focus:ring-primary"
                    required
                  />
                </div>
                <div>
                  <label htmlFor="empresa" className="sr-only">Empresa</label>
                  <input
                    type="text"
                    id="empresa"
                    name="empresa"
                    placeholder="Empresa"
                    value={formData.empresa}
                    onChange={handleChange}
                    className="border border-gray-300 rounded-lg p-3 w-full focus:outline-none focus:ring-2 focus:ring-primary"
                  />
                </div>
                <div className="flex gap-2">
                  <label htmlFor="prefijo" className="sr-only">Código de país</label>
                  <input
                    type="text"
                    id="prefijo"
                    name="prefijo"
                    value={formData.prefijo}
                    onChange={handlePrefijoChange}
                    inputMode="tel"
                    pattern="\+[0-9]{1,4}"
                    title="Código de país, ej: +54"
                    aria-label="Código de país"
                    className="border border-gray-300 rounded-lg p-3 w-16 text-center focus:outline-none focus:ring-2 focus:ring-primary"
                  />
                  <label htmlFor="telefono" className="sr-only">Teléfono</label>
                  <input
                    type="tel"
                    id="telefono"
                    name="telefono"
                    placeholder="Teléfono"
                    value={formData.telefono}
                    onChange={handleTelefonoChange}
                    inputMode="numeric"
                    pattern="[0-9]{6,15}"
                    title="Ingresá solo números (6 a 15 dígitos)"
                    className="border border-gray-300 rounded-lg p-3 w-full focus:outline-none focus:ring-2 focus:ring-primary"
                  />
                </div>
                <div>
                  <label htmlFor="email" className="sr-only">Email</label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    placeholder="Email"
                    value={formData.email}
                    onChange={handleChange}
                    pattern="[^\s@]+@[^\s@]+\.[^\s@]+"
                    title="Ingresá un email válido (ejemplo@dominio.com)"
                    className="border border-gray-300 rounded-lg p-3 w-full focus:outline-none focus:ring-2 focus:ring-primary"
                    required
                  />
                </div>
              </div>

              <label htmlFor="mensaje" className="sr-only">Mensaje</label>
              <textarea
                id="mensaje"
                name="mensaje"
                placeholder="Mensaje"
                value={formData.mensaje}
                onChange={handleChange}
                className="border border-gray-300 rounded-lg p-3 w-full h-28 mt-4 focus:outline-none focus:ring-2 focus:ring-primary"
                required
              />

              <div className="text-center mt-4">
                <button
                  type="submit"
                  disabled={enviando}
                  className="bg-secondary text-white font-semibold py-3 px-6 rounded-full shadow-lg
                  transition-all duration-300
                  hover:bg-secondary/90 hover:shadow-[0_0_20px_rgba(171,101,173,0.6)]
                  disabled:opacity-60 disabled:cursor-not-allowed"
                >
                  {enviando ? "Enviando..." : "Enviar"}
                </button>
              </div>
            </motion.form>

            {/* Info */}
            <motion.div
              className="w-full md:w-[34%] flex flex-col items-center md:items-start justify-center space-y-6 md:pl-4"
              initial={{ opacity: 0, y: 50 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8, delay: 0.2 }}
            >
              <div className="flex items-center space-x-3 text-lg sm:text-xl">
                <Phone size={28} />
                <span>(+54) 351-7144295</span>
              </div>
              <div className="flex items-center space-x-3 text-lg sm:text-xl">
                <Mail size={28} />
                <span>Sales@coresolutions.com.ar</span>
              </div>

              <div className="flex space-x-6 mt-6">
                <a href="https://www.linkedin.com/company/coresolutionsconsultora/" target="_blank" rel="noopener noreferrer">
                  <Linkedin size={40} className="cursor-pointer hover:scale-110 transition" />
                </a>
                <a href="https://www.instagram.com/coresolutions_consultora/" target="_blank" rel="noopener noreferrer">
                  <Instagram size={40} className="cursor-pointer hover:scale-110 transition" />
                </a>
                <a href="https://www.facebook.com/Coresolutionsconsultora" target="_blank" rel="noopener noreferrer">
                  <Facebook size={40} className="cursor-pointer hover:scale-110 transition" />
                </a>
              </div>
            </motion.div>
          </div>
        </div>

        {/* Somos parte de — centrado en el borde inferior de la sección */}
        <div className="absolute bottom-6 left-1/2 -translate-x-1/2 z-10 flex items-center gap-3.5">
          <p className="text-xs sm:text-sm uppercase tracking-widest text-white/70 whitespace-nowrap">
            Somos parte de
          </p>
          <img
            src="/assets/Alianzas/logo-lazos.png"
            alt="Lazos"
            className="h-8 sm:h-10 object-contain"
          />
          <img
            src="/assets/Alianzas/logo-unaje.png"
            alt="Unaje"
            className="h-8 sm:h-10 object-contain"
          />
        </div>
      </section>
    </>
  );
}